import { useEffect, useMemo } from 'react';
import { useLazyQuery } from '@apollo/react-hooks';
import { useTranslation } from 'react-i18next';
import type { StockCarResponse, StockCarSearchResponse, UseStockCarDetails } from '../types';
import { STOCKCAR_DETAILS_QUERY, STOCKCAR_SEARCH_QUERY } from '../schemas/StockCarSchema';
import { getStockIdentifierValues } from '../utils/getStockIdentifierValues';
import { StatImport } from '../constants';

const useStockCarDetails: UseStockCarDetails = (vehicleId, market) => {
  const { i18n } = useTranslation();
  const lang = i18n.languages[0] || 'en';
  const isUsedVehicles = market.includes('uc');
  const isUsMarket = market.includes('us');

  const isEncodedVehicleId = useMemo(() => {
    return vehicleId?.length && vehicleId.length > 17 && vehicleId.endsWith('=');
  }, [vehicleId]);

  const statImport = useMemo(
    () => (isUsMarket ? StatImport.AGC_USA_JDP : StatImport.AGC_CAN_JDP),
    [isUsMarket],
  );

  const [getStockCarDetails, { data: stockCarData, loading: loadingStockCar }] =
    useLazyQuery<StockCarResponse>(STOCKCAR_DETAILS_QUERY, {
      errorPolicy: 'all',
      onError: (error: unknown) => {
        console.error('function getStockCarDetails -> ERROR:', error);
      },
    });

  const [getStockCarSearch, { data: stockCarSearchData, loading: loadingStockCarSearch }] =
    useLazyQuery<StockCarSearchResponse>(STOCKCAR_SEARCH_QUERY, {
      errorPolicy: 'all',
      onError: (error: unknown) => {
        console.error('function getStockCarSearch -> ERROR:', error);
      },
    });

  useEffect(() => {
    if (!vehicleId || !market) return;

    if (isEncodedVehicleId) {
      getStockCarDetails({
        variables: {
          stockCarIdentifier: {
            id: vehicleId,
            stockIdentifier: getStockIdentifierValues(isUsedVehicles, isUsMarket, lang),
          },
        },
      }).catch((error: unknown) => {
        console.error('function getStockCarDetails -> ERROR:', error);
      });
      return;
    }

    getStockCarSearch({
      variables: {
        stockIdentifier: getStockIdentifierValues(isUsedVehicles, isUsMarket, lang),
        searchParameter: {
          criteria: [
            { id: 't_vin', items: [vehicleId] },
            { id: 'stat-import', items: [statImport] },
          ],
        },
      },
    }).catch((error: unknown) => {
      console.error('function getStockCarSearch -> ERROR:', error);
    });
  }, [
    getStockCarDetails,
    vehicleId,
    market,
    lang,
    isEncodedVehicleId,
    isUsedVehicles,
    isUsMarket,
    getStockCarSearch,
    statImport,
  ]);

  const stockCarDetailsResult =
    stockCarData?.stockCar || stockCarSearchData?.stockCarSearch.results.cars[0]?.stockCar;

  const loading = loadingStockCar || loadingStockCarSearch;

  return {
    vehicle: stockCarDetailsResult,
    loading,
    market,
    oaddDealerId: undefined,
  };
};

export default useStockCarDetails;
